import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import {
  useGetMyPoliciesQuery,
  useAcknowledgePolicyMutation,
  useLazyGetPolicyVersionFileQuery,
} from '../../store/api/localApi';
import { useAuth } from '../../hooks/useAuth';
import { AcknowledgeModal } from '../../components/policies/AcknowledgeModal';
import { ParentAction, ParentSafePolicy, StaffPolicyRow } from '../../types';
import { formatDublinDate, formatDublinTime } from '../../utils/date';

const PolicyPdfViewer = React.lazy(() => import('../../components/policies/PolicyPdfViewer'));

interface PendingAck {
  policyId: string;
  policyTitle: string;
  parentAction: ParentAction;
  childId?: string;
  childName?: string;
}

/**
 * A parent's outstanding asks regrouped BY CHILD, ordered by child name — the
 * "Waiting on you" section. Staff rows carry no per-child asks and are skipped.
 */
export function groupOutstandingByChild(policies: Array<ParentSafePolicy | StaffPolicyRow>) {
  const byChild = new Map<
    string,
    {
      childId: string;
      childName: string;
      items: Array<{ policyId: string; title: string; parentAction: ParentAction }>;
    }
  >();
  for (const p of policies) {
    if (!('outstandingForChildren' in p)) continue;
    for (const c of p.outstandingForChildren) {
      const group = byChild.get(c.childId) ?? {
        childId: c.childId,
        childName: c.childName,
        items: [],
      };
      group.items.push({
        policyId: p.policyId,
        title: p.title,
        parentAction: p.currentVersion?.parentAction ?? ParentAction.ACKNOWLEDGE,
      });
      byChild.set(c.childId, group);
    }
  }
  return Array.from(byChild.values()).sort((a, b) => a.childName.localeCompare(b.childName));
}

/**
 * "Policies" for whoever is signed in.
 *
 * PARENT: a "Waiting on you" section first, grouped BY CHILD (a family page —
 * it never reads or changes the active child; `?child=<id>` only scrolls to
 * that child's group). Below it, the cards are grouped BY POLICY, listing the
 * children still to confirm. The ask is per child, but the READING is per
 * policy — a parent with three children should see one card with three names,
 * not three unrelated items.
 *
 * STAFF: their own outstanding acknowledgements at the active location.
 *
 * Nothing on this page blocks anything. Outstanding items are a prompt; a
 * parent can read every policy and leave without responding, and the app
 * continues to work exactly as before.
 */
const MyPolicies: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading } = useGetMyPoliciesQuery();
  const [acknowledge, { isLoading: isAcking }] = useAcknowledgePolicyMutation();
  const [fetchFile, fileResult] = useLazyGetPolicyVersionFileQuery();
  const [pending, setPendingState] = useState<PendingAck | null>(null);
  const [ackError, setAckError] = useState<string | null>(null);
  // Opening, switching or closing the modal starts it without a stale refusal.
  const setPending = (next: PendingAck | null) => {
    setAckError(null);
    setPendingState(next);
  };
  const [openViewer, setOpenViewer] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const scrollTo = searchParams.get('child');
  // The param is kept (not stripped) so a reload still lands on the group.
  useEffect(() => {
    if (!scrollTo || isLoading) return;
    // Matched by attribute value, not by interpolating the URL param into a
    // selector: a crafted ?child= would make querySelector throw.
    const target = `policies-waiting-child-${scrollTo}`;
    Array.from(document.querySelectorAll<HTMLElement>('[data-testid^="policies-waiting-child-"]'))
      .find((el) => el.getAttribute('data-testid') === target)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [scrollTo, isLoading]);

  const isParentShape = (p: unknown): p is ParentSafePolicy =>
    typeof p === 'object' && p !== null && 'outstandingForChildren' in p;

  const policies = data?.policies ?? [];
  const waiting = groupOutstandingByChild(policies);
  // Must match the server's userDisplayName() exactly — it falls back to the
  // email when no name is set (placeholder parents created at enrollment have
  // empty names until they accept their invite). Without the same fallback the
  // signature check would compare the typed name against '' and no such user
  // could ever sign.
  const defaultName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || (user?.email ?? '');

  const handleConfirm = async (signedName?: string) => {
    if (!pending) return;
    try {
      await acknowledge({
        policyId: pending.policyId,
        childId: pending.childId,
        signedName,
      }).unwrap();
      setPending(null);
    } catch (error) {
      // The list can be stale: the child may have been moved out of the
      // policy's classes (POLICY_NOT_FOR_CHILD) or have left the service
      // (CHILD_LEFT_ACTION_CLOSED) since it loaded. Both refusals carry a
      // sentence for the parent; anything else gets a generic line.
      const message = (error as { data?: { message?: unknown } })?.data?.message;
      setAckError(
        typeof message === 'string' && message.trim()
          ? message
          : "Your response couldn't be saved. Please try again."
      );
    }
  };

  const openDocument = async (policyId: string, versionId: string) => {
    setOpenViewer(policyId);
    await fetchFile({ policyId, versionId });
  };

  if (isLoading) {
    return (
      <div data-testid="my-policies-loading" className="py-10 text-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5" data-testid="my-policies">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Policies</h1>
          <p className="mt-1 text-sm text-gray-600">
            The policies your service has published. Anything waiting on you is marked below.
          </p>
        </div>

        {waiting.length > 0 && (
          <section
            data-testid="policies-waiting"
            className="rounded-lg border border-amber-200 bg-amber-50 p-4"
          >
            <h2 className="text-sm font-semibold text-amber-900">Waiting on you</h2>
            <div className="mt-3 space-y-3">
              {waiting.map((group) => (
                <div key={group.childId} data-testid={`policies-waiting-child-${group.childId}`}>
                  <p className="text-sm font-medium text-gray-900">{group.childName}</p>
                  <ul className="mt-1 space-y-1">
                    {group.items.map((item) => (
                      <li
                        key={item.policyId}
                        className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2"
                      >
                        <span className="text-sm text-gray-800">{item.title}</span>
                        <button
                          type="button"
                          data-testid={`policies-waiting-ask-${item.policyId}-${group.childId}`}
                          onClick={() =>
                            setPending({
                              policyId: item.policyId,
                              policyTitle: item.title,
                              parentAction: item.parentAction,
                              childId: group.childId,
                              childName: group.childName,
                            })
                          }
                          className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
                        >
                          {item.parentAction === ParentAction.SIGN ? 'Sign' : 'Confirm'}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {policies.length === 0 && (
          <p data-testid="my-policies-empty" className="text-sm text-gray-500">
            No policies have been published yet.
          </p>
        )}

        <ul className="space-y-4">
          {policies.map((p) => {
            const parentShape = isParentShape(p);
            const policy = p as ParentSafePolicy & StaffPolicyRow;
            const versionId = parentShape
              ? policy.currentVersion?.versionId
              : policy.currentVersion.versionId;
            const outstandingChildren = parentShape ? policy.outstandingForChildren : [];
            // Optional on the wire: a backend older than this release sends no
            // receipts, and the page must still render its policies.
            const signedChildren = parentShape ? policy.signedForChildren ?? [] : [];
            const staffOutstanding = !parentShape && policy.outstanding;

            return (
              <li
                key={policy.policyId}
                data-testid="my-policy-card"
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <DocumentTextIcon
                    aria-hidden="true"
                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">{policy.title}</p>
                    {policy.summary && (
                      <p className="mt-0.5 text-sm text-gray-600">{policy.summary}</p>
                    )}

                    <button
                      type="button"
                      data-testid={`policy-view-${policy.policyId}`}
                      onClick={() => versionId && openDocument(policy.policyId, versionId)}
                      className="mt-2 text-sm font-medium text-primary-700 underline"
                    >
                      Read the policy
                    </button>

                    {/* Parent: one row per child still to confirm. */}
                    {outstandingChildren.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {outstandingChildren.map((c) => (
                          <li
                            key={c.childId}
                            data-testid={`policy-waiting-${policy.policyId}-${c.childId}`}
                            className="flex items-center justify-between gap-3 rounded-md bg-amber-50 px-3 py-2"
                          >
                            <span className="text-sm text-amber-900">
                              Waiting on you for {c.childName}
                            </span>
                            <button
                              type="button"
                              data-testid={`policy-ask-${policy.policyId}-${c.childId}`}
                              onClick={() =>
                                setPending({
                                  policyId: policy.policyId,
                                  policyTitle: policy.title,
                                  parentAction:
                                    policy.currentVersion?.parentAction ?? ParentAction.ACKNOWLEDGE,
                                  childId: c.childId,
                                  childName: c.childName,
                                })
                              }
                              className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
                            >
                              {policy.currentVersion?.parentAction === ParentAction.SIGN
                                ? 'Sign'
                                : 'Confirm'}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Parent: the receipt for each child already done. In-app
                        only — this page is the parent's sole confirmation, so
                        the card stays even when nothing is outstanding. */}
                    {parentShape && signedChildren.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {signedChildren.map((s) => (
                          <li
                            key={s.childId}
                            data-testid={`my-policy-signed-${policy.policyId}-${s.childId}`}
                            className="flex items-center gap-2 text-sm text-gray-600"
                          >
                            <CheckCircleIcon
                              className="h-4 w-4 flex-shrink-0 text-emerald-600"
                              aria-hidden="true"
                            />
                            <span>
                              {s.childName} — {s.ackType === 'SIGN' ? 'Signed' : 'Confirmed'} by{' '}
                              {s.signedByName} on {formatDublinDate(s.acknowledgedAt)} at{' '}
                              {formatDublinTime(s.acknowledgedAt)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Staff: a single outstanding acknowledgement. */}
                    {staffOutstanding && (
                      <div className="mt-3 flex items-center justify-between gap-3 rounded-md bg-amber-50 px-3 py-2">
                        <span className="text-sm text-amber-900">
                          You have not acknowledged this version
                        </span>
                        <button
                          type="button"
                          data-testid={`policy-ask-${policy.policyId}`}
                          onClick={() =>
                            setPending({
                              policyId: policy.policyId,
                              policyTitle: policy.title,
                              parentAction: ParentAction.ACKNOWLEDGE,
                            })
                          }
                          className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
                        >
                          Acknowledge
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {openViewer === policy.policyId && fileResult.data?.url && (
                  <div className="mt-4">
                    <React.Suspense
                      fallback={
                        <div
                          data-testid="policy-pdf-loading"
                          className="py-10 text-center text-sm text-gray-500"
                        >
                          Loading the document viewer…
                        </div>
                      }
                    >
                      <PolicyPdfViewer url={fileResult.data.url} />
                    </React.Suspense>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <AcknowledgeModal
          open={pending !== null}
          policyTitle={pending?.policyTitle ?? ''}
          parentAction={pending?.parentAction ?? ParentAction.ACKNOWLEDGE}
          childName={pending?.childName}
          defaultName={defaultName}
          submitting={isAcking}
          error={ackError}
          onCancel={() => setPending(null)}
          onConfirm={handleConfirm}
        />
      </div>
    </div>
  );
};

export default MyPolicies;
